import {
  Truck,
  ClipboardList,
  Box,
  TruckIcon,
  Users,
  Route,
  MapPin,
  Settings,
  LucideTruck,
} from 'lucide-react';

import { AssetDetailView } from './views/Assets/AssetDetailView';
import { AssetsListView } from './views/Assets/AssetsListView';
import { AssetTypesListView } from './views/Assets/AssetTypesListView';
import { DriversListView } from './views/DriversListView';
import { LoadBoardView } from './views/LoadBoardView';
import { MapView as TransportationMapView } from './views/MapView';
import { RoutesListView } from './views/RoutesListView';
import { DetentionsListView } from './views/Settings/DetentionsListView';
import { FreightLanesListView } from './views/Settings/FreightLanesListView';
import { ServiceTypesListView } from './views/Settings/ServiceTypesListView';
import { TrailersListView } from './views/TrucksAndTrailers/TrailersListView';
import { TransportationTypesListView } from './views/TrucksAndTrailers/TransportationTypesListView';
import { TrucksListView } from './views/TrucksAndTrailers/TrucksListView';

import { createAppModule } from '~/shared/routing/createAppModule';
import { createModuleNav } from '~/shared/routing/createModuleNav';
import { createModuleRoutes } from '~/shared/routing/createModuleRoutes';

const basePath = '/transportation';

export const transportationRoutes = createModuleRoutes({
  root: {
    path: basePath,
    label: (t) => t.menu.transportation.root,
    icon: Truck,
  },
  loadBoard: {
    path: `${basePath}/load-board`,
    label: (t) => t.menu.transportation.loadBoard,
    icon: ClipboardList,
    component: LoadBoardView,
  },
  drivers: {
    path: `${basePath}/drivers`,
    label: (t) => t.menu.transportation.drivers,
    icon: Users,
    component: DriversListView,
    requiredPermission: 'driverRead',
  },
  routes: {
    path: `${basePath}/routes`,
    label: (t) => t.menu.transportation.routes,
    icon: Route,
    component: RoutesListView,
    requiredPermission: 'routeRead',
  },
  map: {
    path: `${basePath}/map`,
    label: (t) => t.menu.transportation.map,
    icon: MapPin,
    component: TransportationMapView,
  },
  trucksAndTrailers: {
    path: `${basePath}/trucks-n-trailers`,
    label: (t) => t.menu.transportation.trucksAndTrailers.root,
    icon: TruckIcon,
  },
  trucks: {
    path: `${basePath}/trucks-n-trailers/trucks`,
    label: (t) => t.menu.transportation.trucksAndTrailers.trucks,
    component: TrucksListView,
    requiredPermission: 'truckRead',
  },
  trailers: {
    path: `${basePath}/trucks-n-trailers/trailers`,
    label: (t) => t.menu.transportation.trucksAndTrailers.trailers,
    component: TrailersListView,
    requiredPermission: 'trailerRead',
  },
  transportationTypes: {
    path: `${basePath}/trucks-n-trailers/transportation-types`,
    label: (t) => t.menu.transportation.trucksAndTrailers.transportationTypes,
    component: TransportationTypesListView,
    requiredPermission: 'transportationTypeRead',
  },
  assets: {
    path: `${basePath}/assets`,
    label: (t) => t.menu.transportation.assets.root,
    icon: Box,
  },
  assetsList: {
    path: `${basePath}/assets/list`,
    label: (t) => t.menu.transportation.assets.list,
    component: AssetsListView,
    requiredPermission: 'assetRead',
  },
  assetDetail: {
    path: `${basePath}/assets/:assetId`,
    component: AssetDetailView,
  },
  assetsTypes: {
    path: `${basePath}/assets/types`,
    label: (t) => t.menu.transportation.assets.types,
    component: AssetTypesListView,
    requiredPermission: 'assetTypeRead',
  },
  freight: {
    path: `${basePath}/freight`,
    label: (t) => t.menu.transportation.freight.root,
    icon: LucideTruck,
  },
  freightLanes: {
    path: `${basePath}/freight/lanes`,
    label: (t) => t.menu.transportation.freight.lanes,
    component: FreightLanesListView,
  },
  freightDetentions: {
    path: `${basePath}/freight/detentions`,
    label: (t) => t.menu.transportation.freight.detentions,
    component: DetentionsListView,
  },
  settings: {
    path: `${basePath}/settings`,
    label: (t) => t.menu.transportation.settings.root,
    icon: Settings,
  },
  serviceTypes: {
    path: `${basePath}/settings/service-types`,
    label: (t) => t.menu.transportation.settings.serviceTypes,
    component: ServiceTypesListView,
    requiredPermission: 'serviceTypeRead',
  },
});

const transportationNav = createModuleNav(($) =>
  $(transportationRoutes.root, [
    $(transportationRoutes.loadBoard),
    $(transportationRoutes.drivers),
    $(transportationRoutes.routes),
    $(transportationRoutes.map),
    $(transportationRoutes.trucksAndTrailers, [
      $(transportationRoutes.trucks),
      $(transportationRoutes.trailers),
      $(transportationRoutes.transportationTypes),
    ]),
    $(transportationRoutes.assets, [
      $(transportationRoutes.assetsList),
      $(transportationRoutes.assetsTypes),
      $(transportationRoutes.assetDetail),
    ]),
    $(transportationRoutes.freight, [
      $(transportationRoutes.freightLanes),
      $(transportationRoutes.freightDetentions),
    ]),
    $(transportationRoutes.settings, [$(transportationRoutes.serviceTypes)]),
  ]),
);

export const transportationModule = createAppModule(
  {
    routes: transportationRoutes,
    nav: transportationNav,
    layout: undefined,
    requiredEntitlement: 'transportation',
  }, // required entitlement
);
